import { useCallback } from 'react';

type CookieOptions = {
  expirationMinutes?: number;
  expirationHours?: number; // Default: 24 hours
  path?: string; // Default: "/"
  domain?: string; // Optional override
};

type UseCookieManagerReturn = {
  getCookie: (key: string) => string | null;
  setCookie: (key: string, value: string) => void;
  removeCookie: (key: string) => void;
};

const useCookieManager = ({
  expirationMinutes,
  expirationHours = 24,
  path = '/',
  domain,
}: CookieOptions = {}): UseCookieManagerReturn => {
  const isBrowser = typeof window !== 'undefined';
  const resolvedDomain = isBrowser ? domain || window.location.hostname : domain || '';

  const buildCookieString = useCallback(
    (key: string, value: string, expirationDate: Date) => {
      const cookieParts = [
        `${key}=${encodeURIComponent(value)}`,
        `expires=${expirationDate.toUTCString()}`,
        `path=${path}`,
        'SameSite=Lax',
      ];

      if (resolvedDomain) {
        cookieParts.push(`domain=${resolvedDomain}`);
      }

      return cookieParts.join('; ');
    },
    [path, resolvedDomain]
  );

  const setCookie = useCallback(
    (key: string, value: string) => {
      if (!isBrowser) return;

      const expiration = new Date();
      if (expirationMinutes != null) {
        expiration.setTime(expiration.getTime() + expirationMinutes * 60 * 1000);
      } else {
        expiration.setHours(expiration.getHours() + expirationHours);
      }

      document.cookie = buildCookieString(key, value, expiration);
    },
    [expirationMinutes, expirationHours, isBrowser, buildCookieString]
  );

  const getCookie = useCallback(
    (key: string): string | null => {
      if (!isBrowser) return null;

      const cookies = document.cookie.split('; ');
      const cookie = cookies.find((c) => c.startsWith(`${key}=`));
      return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
    },
    [isBrowser]
  );

  const removeCookie = (name: string) => {
    if (typeof document === 'undefined') return;

    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;

    if (resolvedDomain) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${resolvedDomain}`;
    }
  };

  return { getCookie, setCookie, removeCookie };
};

export default useCookieManager;
